import type {
  PatientDossierFamilyHistoryItem,
  PatientDossierPriorDelivery,
} from '../../../../../helpers/api/patients/patients';
import {
  FAMILY_CONDITIONS,
  type PredefinedFamilyConditionCode,
} from '../constants';
import type {
  CoreData,
  CurrentPregnancyData,
  DisabilityState,
  FamilyConditionState,
  FamilyMemberCode,
  HistoryListState,
  HistorySection,
  OtherFamilyState,
  PreventiveCareData,
  PriorDeliveryRowState,
} from '../types';

export const buildPatientPayload = (
  core: CoreData
): Record<string, unknown> => {
  const out: Record<string, unknown> = {
    patient_name: core.patient_name.trim(),
    patient_last_name: core.patient_last_name.trim(),
  };

  const maybeString = (key: keyof CoreData, target = String(key)) => {
    const v = String(core[key] ?? '').trim();
    if (v) {
      out[target] = v;
    }
  };

  maybeString('patient_middle_name');
  if (core.birth_date) {
    out.birth_date = core.birth_date;
  }
  if (core.age_years !== null && Number.isFinite(core.age_years)) {
    out.age_years = core.age_years;
    out.age_as_of_date = new Date().toISOString();
  }
  maybeString('cin');
  maybeString('address');
  if (core.education_level) {
    out.education_level = core.education_level;
  }
  maybeString('occupation');
  if (core.coverage_mode) {
    out.coverage_mode = core.coverage_mode;
  }
  maybeString('medical_coverage_label');
  maybeString('woman_origin');
  if (core.marital_status) {
    out.marital_status = core.marital_status;
  }
  if (core.consanguinity !== null) {
    out.consanguinity = core.consanguinity;
  }
  maybeString('spouse_name');
  maybeString('spouse_occupation');
  if (core.habit_caffeine !== null) {
    out.habit_caffeine = core.habit_caffeine;
  }
  if (core.habit_alcohol !== null) {
    out.habit_alcohol = core.habit_alcohol;
  }
  if (core.habit_tobacco !== null) {
    out.habit_tobacco = core.habit_tobacco;
  }
  if (core.habit_medications !== null) {
    out.habit_medications = core.habit_medications;
  }
  if (core.habit_geophagy !== null) {
    out.habit_geophagy = core.habit_geophagy;
  }
  maybeString('habit_other_notes');
  if (core.blood_group_abo) {
    out.blood_group_abo = core.blood_group_abo;
  }
  if (core.blood_group_rhesus) {
    out.blood_group_rhesus = core.blood_group_rhesus;
  }
  if (core.husband_blood_group_abo) {
    out.husband_blood_group_abo = core.husband_blood_group_abo;
  }
  if (core.husband_blood_group_rhesus) {
    out.husband_blood_group_rhesus = core.husband_blood_group_rhesus;
  }
  if (core.weight_kg !== null && Number.isFinite(core.weight_kg)) {
    out.weight_kg = core.weight_kg;
  }
  if (core.height_cm !== null && Number.isFinite(core.height_cm)) {
    out.height_cm = core.height_cm;
  }
  if (core.phone_number) {
    out.phone_number = core.phone_number;
  }
  maybeString('email');
  // Always sent, never omitted when false: this is the one field where the
  // absence of a value and an explicit "no" mean different things to the
  // reminder sweep.
  out.reminders_opt_out = !!core.reminders_opt_out;
  return out;
};

const encodeMembers = (members: FamilyMemberCode[]): string | null => {
  if (!members || members.length === 0) {
    return null;
  }
  return JSON.stringify({ members });
};

const encodeOtherSide = (
  side: 'wife_members' | 'husband_members',
  entries: OtherFamilyState['entries']
): string | null => {
  const payload = entries
    .map((e) => ({
      label: (e.label || '').trim(),
      members: e[side] ?? [],
    }))
    .filter((e) => e.label.length > 0 || e.members.length > 0);
  if (payload.length === 0) {
    return null;
  }
  return JSON.stringify({ entries: payload });
};

export const buildFamilyHistory = (
  family: Record<PredefinedFamilyConditionCode, FamilyConditionState>,
  otherFamily: OtherFamilyState
): PatientDossierFamilyHistoryItem[] => {
  const rows: PatientDossierFamilyHistoryItem[] = [];

  for (const cond of FAMILY_CONDITIONS) {
    const state = family[cond.code];
    if (!state.active) {
      continue;
    }
    const wifeNotes = encodeMembers(state.wife_members);
    const husbandNotes = encodeMembers(state.husband_members);
    rows.push({
      condition_code: cond.code,
      wife_family_notes: wifeNotes,
      husband_family_notes: husbandNotes,
    });
  }

  // OTHER bucket — only emit a row when the user actually filled an entry.
  if (otherFamily.active && otherFamily.entries.length > 0) {
    const wifeJson = encodeOtherSide('wife_members', otherFamily.entries);
    const husbandJson = encodeOtherSide('husband_members', otherFamily.entries);
    if (wifeJson || husbandJson) {
      rows.push({
        condition_code: 'OTHER',
        wife_family_notes: wifeJson,
        husband_family_notes: husbandJson,
      });
    }
  }

  return rows;
};

export const buildPatientMedicalHistory = (
  historyLists: Record<HistorySection, HistoryListState>,
  disability: DisabilityState,
  preventiveCare: PreventiveCareData
): Record<string, unknown> | null => {
  const serializeList = (state: HistoryListState): string | null => {
    if (!state.active) {
      return null;
    }
    const items = state.items.map((i) => i.trim()).filter(Boolean);
    return items.length ? items.join('\n') : null;
  };

  const payload: Record<string, unknown> = {};
  const med = serializeList(historyLists.medical);
  const sur = serializeList(historyLists.surgical);
  const gyn = serializeList(historyLists.gynecological);
  if (med) {
    payload.medical_hx = med;
  }
  if (sur) {
    payload.surgical_hx = sur;
  }
  if (gyn) {
    payload.gynecological_hx = gyn;
  }

  if (disability.types.length > 0) {
    payload.disability_type = disability.types.join(',');
    if (
      disability.types.includes('OTHER') &&
      disability.other_description.trim()
    ) {
      payload.disability_other_description =
        disability.other_description.trim();
    }
  }

  if (preventiveCare.vat_dose !== null) {
    payload.vat_dose = preventiveCare.vat_dose;
  }
  if (preventiveCare.rubella_vaccinated !== null) {
    payload.rubella_vaccinated = preventiveCare.rubella_vaccinated;
  }
  if (
    preventiveCare.rubella_vaccinated &&
    preventiveCare.rubella_vaccination_date
  ) {
    payload.rubella_vaccination_date =
      preventiveCare.rubella_vaccination_date;
  }
  if (preventiveCare.hepatitis_b_vaccinated !== null) {
    payload.hepatitis_b_vaccinated = preventiveCare.hepatitis_b_vaccinated;
  }
  if (
    preventiveCare.hepatitis_b_vaccinated &&
    preventiveCare.hepatitis_b_vaccination_date
  ) {
    payload.hepatitis_b_vaccination_date =
      preventiveCare.hepatitis_b_vaccination_date;
  }
  if (preventiveCare.cervical_smear_iva_date) {
    payload.cervical_smear_iva_date = preventiveCare.cervical_smear_iva_date;
  }
  return Object.keys(payload).length ? payload : null;
};

export const buildPriorDeliveries = (
  priorDeliveries: PriorDeliveryRowState[]
): PatientDossierPriorDelivery[] =>
  priorDeliveries.map((row, idx) => {
    const hasAnomaly = row.has_obstetric_anomalies && !!row.anomaly_type;
    const isNormal = !row.has_obstetric_anomalies;
    const isStillbirth = row.newborn_vital_status === 'STILLBIRTH';
    return {
      delivery_index: idx + 1,
      delivery_date: row.delivery_date || null,
      delivery_type: isNormal ? row.delivery_type || null : null,
      delivery_ga_weeks: isNormal ? row.delivery_ga_weeks : null,
      delivery_ga_days: isNormal ? row.delivery_ga_days : null,
      pregnancy_term_status: isNormal
        ? row.pregnancy_term_status || null
        : null,
      extraction_modalities: row.extraction_modalities.trim() || null,
      cesarean_indication:
        isNormal && row.delivery_type === 'CESAREAN'
          ? row.cesarean_indication.trim() || null
          : null,
      delivery_place: isNormal ? row.delivery_place.trim() || null : null,
      delivery_complication_type: isNormal
        ? row.delivery_complication_type.trim() || null
        : null,
      newborn_complication_type:
        isNormal && !isStillbirth
          ? row.newborn_complication_type.trim() || null
          : null,
      newborn_weight_grams: isNormal ? row.newborn_weight_grams : null,
      newborn_sex: isNormal ? row.newborn_sex || null : null,
      newborn_vital_status: isNormal ? row.newborn_vital_status || null : null,
      has_obstetric_anomalies: row.has_obstetric_anomalies,
      anomaly_type: hasAnomaly ? row.anomaly_type || null : null,
      anomaly_count: null,
      anomaly_date: hasAnomaly ? row.anomaly_date || null : null,
      anomaly_place: hasAnomaly ? row.anomaly_place.trim() || null : null,
      anomaly_ga_weeks: hasAnomaly ? row.anomaly_ga_weeks : null,
      anomaly_description: hasAnomaly ? buildAnomalyDescription(row) : null,
    };
  });

const buildAnomalyDescription = (row: PriorDeliveryRowState): string | null => {
  const desc = row.anomaly_description.trim();
  if (
    row.anomaly_ga_days !== null &&
    row.anomaly_ga_days > 0 &&
    row.anomaly_ga_weeks !== null
  ) {
    const suffix = `[ga_days=${row.anomaly_ga_days}]`;
    return desc ? `${desc} ${suffix}` : suffix;
  }
  return desc || null;
};

export const buildPregnancy = (
  pregnancy: CurrentPregnancyData
): Record<string, unknown> | null => {
  const out: Record<string, unknown> = {};
  if (pregnancy.grossesse_type.trim()) {
    out.grossesse_type = pregnancy.grossesse_type.trim();
  }
  if (pregnancy.grossesse_classee_a_risque !== null) {
    out.grossesse_classee_a_risque = pregnancy.grossesse_classee_a_risque;
    out.risque_anemie = pregnancy.risque_anemie;
    out.risque_hta = pregnancy.risque_hta;
    out.risque_diabete = pregnancy.risque_diabete;
    out.risque_cardiopathie = pregnancy.risque_cardiopathie;
    out.risque_metrorragie = pregnancy.risque_metrorragie;
    out.risque_infection = pregnancy.risque_infection;
    out.risque_preeclampsie = pregnancy.risque_preeclampsie;
    out.risque_eclampsie = pregnancy.risque_eclampsie;
    out.risque_autre_details =
      pregnancy.risque_autre_details.trim() || null;
  }
  if (pregnancy.last_menstrual_period) {
    out.last_menstrual_period = pregnancy.last_menstrual_period;
    out.dating_method = 'LMP';
  }
  if (pregnancy.estimated_due_date) {
    out.estimated_due_date = pregnancy.estimated_due_date;
    if (!out.dating_method) {
      out.dating_method = 'EDD';
    }
  }
  if (pregnancy.lmp_is_estimated) {
    out.lmp_is_estimated = true;
  }
  if (pregnancy.edd_is_estimated) {
    out.edd_is_estimated = true;
  }
  if (pregnancy.grossesse_desiree !== null) {
    out.grossesse_desiree = pregnancy.grossesse_desiree;
  }
  if (pregnancy.preg_crl !== null) {
    out.preg_crl = pregnancy.preg_crl;
  }
  if (pregnancy.preg_bpd !== null) {
    out.preg_bpd = pregnancy.preg_bpd;
  }
  if (pregnancy.preg_hc !== null) {
    out.preg_hc = pregnancy.preg_hc;
  }
  if (pregnancy.preg_ac !== null) {
    out.preg_ac = pregnancy.preg_ac;
  }
  if (pregnancy.preg_fl !== null) {
    out.preg_fl = pregnancy.preg_fl;
  }
  return Object.keys(out).length ? out : null;
};
